// Whether the product page should offer "Request Refund" on a purchase.
//
// This exists because the gate used to be one expression on the product page:
//
//     pricingModel === 'pwyw' || (price > 0 && pricingModel !== 'free')
//
// `price` is the STANDARD price. A tiered product never sets it, because the
// money lives in `priceTiers`, so every tiered purchase read as free and the
// refund action was hidden from buyers who had genuinely paid, on both one-off and
// recurring tiers. The server has always accepted those requests (submitDispute
// resolves the charge from the entitlement and blocks on what was actually
// paid, not on the list price); only the button was missing.
//
// The rule here mirrors the server's: a refund is offered when the seller
// enabled a refund policy AND the buyer parted with money. `pricePaid` from the
// buyer's entitlement (users/{uid}/ownedProducts/{productId}) answers the second
// half exactly, so it wins whenever it is known. The per-pricing-model reading
// of the product doc is the fallback for the cases where it isn't: guests, whose
// entitlement lives in a doc they cannot read, and legacy entitlements written
// before `pricePaid` was recorded.
//
// Pure: no React, no Firestore, no clock. Like tier-actions.ts, the page passes
// in what it has.

export type RefundPricingModel = 'standard' | 'tiered' | 'pwyw' | 'subscription' | 'free';

export interface RefundEligibilityInput {
    pricingModel?: RefundPricingModel;
    /** The product doc's `price`. Standard price for most models; mirrors the per-cycle price on subscriptions. */
    price?: unknown;
    /** Per-cycle price of a whole-product subscription. */
    subscriptionPrice?: unknown;
    /** Tiers of a tiered product. Prices are stored as numbers by create, as strings by the editor. */
    priceTiers?: readonly { id?: unknown; price?: unknown }[] | null;
    /** Tier the buyer actually holds, when they hold one. */
    purchasedTierId?: unknown;
    /**
     * What the buyer paid for the charge under dispute, from their entitlement.
     * Undefined when unknown (guest, or a legacy entitlement), never 0 for it.
     */
    pricePaid?: unknown;
    /** `details.refundPolicy.enabled` on the product doc. */
    refundPolicyEnabled?: boolean;
}

/** Numeric price from a field that may hold a number, a numeric string, or nothing. */
function toPrice(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Whether this purchase involved money, and so has something to reverse.
 *
 * Free products, free tiers, and any $0 acquisition read as false, because the server
 * rejects those requests outright ("Refunds are not available for free
 * products"), so offering the action would only produce an error.
 */
export function purchaseWasPaid(input: RefundEligibilityInput): boolean {
    // Authoritative when present: this is the figure submitDispute gates on.
    // Guards against both directions of drift between the product doc and the
    // order. A seller who later drops a price to zero has not made an earlier
    // sale unrefundable, and a free tier is not made refundable by a paid one
    // sitting next to it.
    if (input.pricePaid !== undefined && input.pricePaid !== null) {
        return toPrice(input.pricePaid) > 0;
    }

    switch (input.pricingModel) {
    case 'free':
        return false;
    // The buyer named their own price, and the minimum may be zero. Whether
    // they paid anything is only knowable from the order, so defer to the
    // server rather than hide the action from everyone who did.
    case 'pwyw':
        return true;
    case 'subscription':
        return toPrice(input.subscriptionPrice) > 0 || toPrice(input.price) > 0;
    case 'tiered': {
        const tiers = input.priceTiers ?? [];
        // Scoped to the tier the buyer holds when we know it. Falling back to
        // "any tier costs money" is deliberately permissive: it is only reached
        // when the tier is unknown, where hiding the action from a paying buyer
        // is the worse error.
        if (input.purchasedTierId !== undefined && input.purchasedTierId !== null && input.purchasedTierId !== '') {
            const owned = tiers.find((t) => String(t?.id) === String(input.purchasedTierId));
            if (owned) return toPrice(owned.price) > 0;
        }
        return tiers.some((t) => toPrice(t?.price) > 0);
    }
    default:
        return toPrice(input.price) > 0;
    }
}

/**
 * Whether to render the "Request Refund" action. Both halves must hold: the
 * seller offers refunds on this product, and there is a payment to reverse.
 */
export function canRequestRefund(input: RefundEligibilityInput): boolean {
    return input.refundPolicyEnabled === true && purchaseWasPaid(input);
}
